<template>
  <Drawer
    :visible="visible"
    position="right"
    class="settings-drawer"
    :showCloseIcon="false"
    aria-label="Table Layout Settings"
    @update:visible="onVisibleChange"
  >
    <h3>{{ t('labels.tableSettingsHdlSize') }}</h3>
    <div class="field-select">
      <label class="hidden">{{ t('labels.tableSettingsFontsize') }}</label>
      <SelectButton
        v-model="selectedSizeValue"
        :options="sizeOptions"
        :allowEmpty="false"
        optionLabel="label"
        optionValue="value"
        data-key="value"
      />
    </div>

    <h3>{{ t('labels.tableSettingsHdlColumns') }}</h3>
    <div v-for="option in sortedColumnOptions" :key="option.field" class="field-checkbox">
      <Checkbox v-model="selectedColumns" :value="option.field" @change="emitColumnChange" />
      <label>{{ t(`labels.${option.label}`) }}</label>
    </div>

    <h3 v-if="dataKey">{{ t('labels.tableSettingsHdlExportCSV') }}</h3>
    <div v-if="dataKey" class="export-options">
      <div class="field-select">
        <label>{{ t('labels.exportDataType') }}</label>
        <SelectButton
          v-model="selectedDataType"
          :options="dataTypeOptions"
          :allowEmpty="false"
          optionLabel="label"
          optionValue="value"
          data-key="value"
        />
      </div>

      <div class="field-select">
        <label>{{ t('labels.exportScope') }}</label>
        <SelectButton
          v-model="selectedExportScope"
          :options="exportScopeOptions"
          :allowEmpty="false"
          optionLabel="label"
          optionValue="value"
          data-key="value"
        />
      </div>

      <div class="field-select">
        <label>{{ t('labels.exportColumnScope') }}</label>
        <SelectButton
          v-model="selectedColumnScope"
          :options="columnScopeOptions"
          :allowEmpty="false"
          optionLabel="label"
          optionValue="value"
          data-key="value"
        />
      </div>

      <div>
        <Button
          :label="t('buttons.exportCSV')"
          icon="pi pi-download"
          class="mt-2"
          @click="emitExport"
        />
      </div>
    </div>

    <h3>{{ t('labels.tableSettingsHdlLayout') }}</h3>
    <!-- Striped Rows Checkbox -->
    <div class="field-checkbox">
      <Checkbox v-model="stripedRows" binary @change="emitChange('stripedRows', stripedRows)" />
      <label>{{ t('labels.tableSettingsStripedRows') }}</label>
    </div>

    <!-- Cell Grid Checkbox -->
    <div class="field-checkbox">
      <Checkbox v-model="cellGrid" binary @change="emitChange('cellGrid', cellGrid)" />
      <label>{{ t('labels.tableSettingsGrid') }}</label>
    </div>

    <!-- Checkbox Multiselect -->
    <div class="field-checkbox">
      <Checkbox
        v-model="checkboxMultiselect"
        binary
        @change="emitChange('checkboxMultiselect', checkboxMultiselect)"
      />
      <label>{{ t('labels.tableSettingsMultiselect') }}</label>
    </div>

    <!-- Footer Pagination -->
    <div class="field-checkbox">
      <Checkbox
        v-model="footerPagination"
        binary
        @change="emitChange('footerPagination', footerPagination)"
      />
      <label>{{ t('labels.tableSettingsFooterPagination') }}</label>
    </div>

    <h3>{{ t('labels.tableSettingsHdlBehaviour') }}</h3>

    <!-- Instant Edit-->
    <div class="field-checkbox">
      <Checkbox v-model="instantEdit" binary @change="emitChange('instantEdit', instantEdit)" />
      <label>{{ t('labels.tableSettingsInstantEdit') }}</label>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import Checkbox from 'primevue/checkbox';
import Drawer from 'primevue/drawer';
import SelectButton from 'primevue/selectbutton';
import { useI18n } from 'vue-i18n';

import { usePreferencesStore } from '@/stores/preferences';
import { useTableLayoutSettingsStore } from '@/stores/tableLayoutSettings';
import { useSizeStore } from '@/stores/tableSize';

import { type ColumnConfig } from './dataTableTypes';

const { t } = useI18n();

const props = defineProps<{
  columns: ColumnConfig[];
  visibleColumns: string[];
  visible: boolean;
  sizeOptions?: { label: string; value: string }[];
  dataKey?: string;
}>();

const columnOptions = computed(() => {
  return props.columns
    .filter((col) => !col.hidden)
    .map((col) => ({
      label: col.title || col.header || col.field,
      field: col.field,
    }));
});

const sizeStore = useSizeStore();
const preferencesStore = usePreferencesStore();
const selectedColumns = ref([...props.visibleColumns]);

const prefixed = (key: string) => `tableSettings.${props.dataKey}.${key}`;

const selectedDataType = computed<'formatted' | 'raw'>({
  get: () =>
    preferencesStore.getPreference(prefixed('selectedDataType'), false) ? 'raw' : 'formatted',
  set: (val) => preferencesStore.setPreference(prefixed('selectedDataType'), val === 'raw'),
});

const selectedExportScope = computed<'all' | 'filtered'>({
  get: () =>
    preferencesStore.getPreference(prefixed('selectedExportScope'), true) ? 'all' : 'filtered',
  set: (val) => preferencesStore.setPreference(prefixed('selectedExportScope'), val === 'all'),
});

const selectedColumnScope = computed<'visible' | 'all'>({
  get: () =>
    preferencesStore.getPreference(prefixed('selectedColumnScope'), false) ? 'all' : 'visible',
  set: (val) => preferencesStore.setPreference(prefixed('selectedColumnScope'), val === 'all'),
});

const dataTypeOptions = computed(() => [
  { label: t('labels.exportFormatted'), value: 'formatted' },
  { label: t('labels.exportRaw'), value: 'raw' },
]);

const exportScopeOptions = computed(() => [
  { label: t('labels.exportAll'), value: 'all' },
  { label: t('labels.exportFiltered'), value: 'filtered' },
]);

const columnScopeOptions = computed(() => [
  { label: t('labels.exportVisibleColumns'), value: 'visible' },
  { label: t('labels.exportAllColumns'), value: 'all' },
]);

const emitExport = () => {
  emit('export', {
    dataType: selectedDataType.value,
    exportScope: selectedExportScope.value,
    columnScope: selectedColumnScope.value,
  });
};

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'update-settings', payload: { key: string; value: boolean }): void;
  (e: 'update:visibleColumns', value: string[]): void;
  (e: 'update:size', value: string): void;
  (
    e: 'export',
    payload: {
      dataType: 'formatted' | 'raw';
      exportScope: 'all' | 'filtered';
      columnScope: 'visible' | 'all';
    },
  ): void;
}>();

const sizeOptions = computed(
  () =>
    props.sizeOptions ?? [
      { label: t('buttons.small'), value: 'small' },
      { label: t('buttons.normal'), value: 'normal' },
      { label: t('buttons.large'), value: 'large' },
    ],
);

const selectedSizeValue = ref(sizeStore.size);

// Zugriff auf den Store
const tableLayoutSettingsStore = useTableLayoutSettingsStore();

const sortedColumnOptions = computed(() => {
  const selectedFieldsSet = new Set(selectedColumns.value);
  const allColumnOptions = columnOptions.value;

  // Sort the selected and remaining columns
  const selectedOptions = selectedColumns.value
    .map((selectedField) => allColumnOptions.find((option) => option.field === selectedField))
    .filter((option) => option !== undefined) as Array<{ label: string; field: string }>;

  const remainingOptions = allColumnOptions.filter(
    (option) => !selectedFieldsSet.has(option.field),
  );

  const allSortingOptions = [...selectedOptions, ...remainingOptions];

  return allSortingOptions;
});

const emitColumnChange = () => {
  emit('update:visibleColumns', selectedColumns.value);
};

// Handle visibility changes
const onVisibleChange = (newValue: boolean) => {
  emit('update:visible', newValue);
};

const emitChange = (key: string, value: boolean) => {
  emit('update-settings', { key, value });
};

const stripedRows = ref(false);
const cellGrid = ref(false);
const checkboxMultiselect = ref(false);
const footerPagination = ref(false);
const instantEdit = ref(false);

watch(selectedSizeValue, (newSize) => {
  sizeStore.setSize(newSize);
  emit('update:size', newSize);
});

watch(stripedRows, (newValue) => {
  tableLayoutSettingsStore.setStripedRows(newValue);
});

watch(cellGrid, (newValue) => {
  tableLayoutSettingsStore.setCellGrid(newValue);
});

watch(checkboxMultiselect, (newValue) => {
  tableLayoutSettingsStore.setCheckboxMultiselect(newValue);
});

watch(footerPagination, (newValue) => {
  tableLayoutSettingsStore.setFooterPagination(newValue);
});

watch(instantEdit, (newValue) => {
  tableLayoutSettingsStore.setInstantEdit(newValue);
});

watch(
  () => props.visibleColumns, // Watch for changes to `props.visibleColumns`
  (newVisibleColumns) => {
    selectedColumns.value = [...newVisibleColumns]; // Update `selectedColumns` with new values
  },
  { immediate: true }, // Execute immediately when component is mounted
);

onMounted(() => {
  stripedRows.value = tableLayoutSettingsStore.stripedRows;
  cellGrid.value = tableLayoutSettingsStore.cellGrid;
  checkboxMultiselect.value = tableLayoutSettingsStore.checkboxMultiselect;
  footerPagination.value = tableLayoutSettingsStore.footerPagination;
  instantEdit.value = tableLayoutSettingsStore.instantEdit;

  selectedColumns.value = [...props.visibleColumns];
  selectedSizeValue.value = sizeStore.size;
});
</script>

<style scoped lang="css">
@reference "tailwindcss";
@reference "tailwindcss/utilities";

@layer app {
  .settings-drawer {
    width: 300px;
    padding: 1rem;
  }

  .field-checkbox {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
  }

  .field-checkbox label {
    margin-left: 0.5rem;
  }

  .field-select {
    /* display: flex; */
    align-items: center;
    margin-bottom: 1rem;
  }

  .field-select label {
    margin-left: 0.5rem;
    @apply mb-2 block;
  }

  h3 {
    @apply mt-8;
  }
}
</style>
